import { useEffect, useRef } from 'react'

function getCentroid(multiPolygonCoords) {
  try {
    let sumX = 0
    let sumY = 0
    let count = 0

    multiPolygonCoords.forEach((polygonGroup) => {
      polygonGroup.forEach((polygon) => {
        polygon.forEach(([x, y]) => {
          sumX += x
          sumY += y
          count++
        })
      })
    })

    return count ? [sumX / count, sumY / count] : null
  } catch (e) {
    console.error('Error in getCentroid:', e)
    return null
  }
}

function getBoundsFromGeoJSON(geojson) {
  const bounds = new window.google.maps.LatLngBounds()

  geojson.features.forEach((feature) => {
    const coords = feature.geometry.coordinates
    coords.forEach((polygon) => {
      polygon[0].forEach(([lng, lat]) => {
        bounds.extend(new window.google.maps.LatLng(lat, lng))
      })
    })
  })

  return bounds
}

function getFeatureCenter(feature) {
  const bounds = new window.google.maps.LatLngBounds()

  feature.geometry.coordinates.forEach((polygonGroup) => {
    polygonGroup.forEach((polygon) => {
      polygon.forEach(([lng, lat]) => {
        bounds.extend(new window.google.maps.LatLng(lat, lng))
      })
    })
  })

  const center = bounds.getCenter()
  return center ? [center.lng(), center.lat()] : null
}


const NYCNeighborhoodMap = ({ mapInstance, visible, isCityVisible, borough = '', neighborhood = '' }) => {
  const markersRef = useRef([])

  const colorMap = {
    Manhattan: '#f7941d',
    Brooklyn: '#8cc63f',
    Queens: '#fcee21',
    Bronx: '#b49fc8',
    'Staten Island': '#f78da7',
  }

  useEffect(() => {
    if (!mapInstance) return

    const clearMap = () => {
      mapInstance.data.forEach((feature) => {
        mapInstance.data.remove(feature)
      })
      markersRef.current.forEach((marker) => marker.setMap(null))
      markersRef.current = []
      mapInstance.data.setStyle(null)
    }

    const fetchAndRenderNeighborhoods = async (selectedBorough) => {
      try {
        const url = `https://data.cityofnewyork.us/resource/9nt8-h7nd.geojson?boroname=${encodeURIComponent(selectedBorough)}`
        const res = await fetch(url)
        const geojson = await res.json()

        clearMap()
        mapInstance.data.addGeoJson(geojson)

        const bounds = getBoundsFromGeoJSON(geojson)
        mapInstance.fitBounds(bounds)
        mapInstance.data.setStyle({
          fillColor: 'transparent',
          strokeColor: '#f7941d',
          strokeWeight: 4,
          strokeOpacity: 1,
        })

        geojson.features.forEach((feature) => {
          const name = feature.properties.ntaname
          const coords = feature.geometry.coordinates
          const center = getCentroid(coords)

          if (center) {
            const marker = new window.google.maps.Marker({
              position: { lat: center[1], lng: center[0] },
              map: mapInstance,
              label: {
                text: name,
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#ffffff',
              },
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 0,
              },
            })

            markersRef.current.push(marker)
          }
        })
      } catch (error) {
        console.error('Failed to fetch neighborhoods from borough click:', error)
      }
    }

    const fetchAndRenderMap = async () => {
      try {
        let geojson = null

        if (!visible && !isCityVisible) {
          clearMap()
          return
        }

        if (isCityVisible) {
          const res = await fetch(
            'https://services5.arcgis.com/GfwWNkhOj9bNBqoJ/arcgis/rest/services/NYC_Borough_Boundary/FeatureServer/0/query?where=1=1&outFields=*&outSR=4326&f=geojson'
          )
          geojson = await res.json()
        } else {
          let baseUrl = 'https://data.cityofnewyork.us/resource/9nt8-h7nd.geojson'
          if (borough) {
            baseUrl += `?boroname=${encodeURIComponent(borough)}`
          } else if (neighborhood) {
            baseUrl += `?$q=${encodeURIComponent(neighborhood)}`
          }

          const res = await fetch(baseUrl)
          geojson = await res.json()
        }

        clearMap()

        mapInstance.data.addGeoJson(geojson)

        mapInstance.data.setStyle((feature) => {
          const boro = feature.getProperty('boroname') || feature.getProperty('BoroName')
        
          console.log('Using borough for style:', boro, colorMap[boro]) // Debug
        
          return {
            fillColor: isCityVisible ? colorMap[boro] || '#ccc' : 'transparent',
            fillOpacity: isCityVisible ? 0.3 : 0,
            strokeColor: colorMap[boro] || '#f7941d',
            strokeOpacity: 1,
            strokeWeight: 4,
          }
        })

        if (isCityVisible) {
          geojson.features.forEach((feature) => {
            const boroName = feature.properties.BoroName
            const coords = feature.geometry.coordinates
            const center = getFeatureCenter(feature)
        
            if (center) {
              const marker = new window.google.maps.Marker({
                position: { lat: center[1], lng: center[0] },
                map: mapInstance,
                label: {
                  text: boroName,
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#ffffff',
                },
                icon: {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 0,
                  labelOrigin: new window.google.maps.Point(0, 0)
                },
              })
        
              markersRef.current.push(marker)
            }
          })
        
          mapInstance.data.addListener('click', (event) => {
            const boroughName = event.feature.getProperty('BoroName')
            if (boroughName) {
              fetchAndRenderNeighborhoods(boroughName)
            }
          })
        }

        if (!isCityVisible) {
          geojson.features.forEach((feature) => {
            const name = feature.properties.ntaname
            const coords = feature.geometry.coordinates
            const center = getCentroid(coords)

            if (center) {
              const marker = new window.google.maps.Marker({
                position: { lat: center[1], lng: center[0] },
                map: mapInstance,
                label: {
                  text: name,
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#ffffff',
                },
                icon: {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 0,
                },
              })

              markersRef.current.push(marker)
            }
          })
        }
      } catch (err) {
        console.error('Failed to fetch map data:', err)
      }
    }

    fetchAndRenderMap()

    return () => {
      clearMap()
    }
  }, [visible, isCityVisible, mapInstance, borough, neighborhood])

  return null
}

export default NYCNeighborhoodMap
